// My account: new password (also after an invite / reset link), two-step sign-in (TOTP app: Google Authenticator,
// Microsoft Authenticator, 1Password …), the code at sign-in, display name, language.
import { rpc, sb } from "../sb.ts";
import { logoUrl, type Brand } from "../brand.ts";
import { getLang, setLang, t } from "../i18n.ts";
import { h, mount, toast } from "../ui.ts";

const strong = (p: string) => p.length >= 10;

/** after an invite or a reset link: the user chooses their password */
export function setPassword(root: HTMLElement, brand: Brand, done: () => void) {
  const pw = h("input", { type: "password", autocomplete: "new-password", required: true, minlength: 10 });
  const pw2 = h("input", { type: "password", autocomplete: "new-password", required: true });
  const msg = h("p", { class: "muted" }, t("At least 10 characters."));
  const logo = logoUrl(brand.logo_path);
  mount(root, h("main", { class: "card narrow" }, logo && h("img", { class: "logo", src: logo, alt: "" }),
    h("h1", {}, t("Choose your password")),
    h("form", { onsubmit: async (e: Event) => {
      e.preventDefault();
      if (!strong(pw.value)) { msg.textContent = t("At least 10 characters."); return; }
      if (pw.value !== pw2.value) { msg.textContent = t("The two passwords are not the same."); return; }
      const { error } = await sb.auth.updateUser({ password: pw.value });
      if (error) { msg.textContent = error.message; return; }
      history.replaceState(null, "", location.pathname);
      toast(t("Password saved"));
      done();
    } }, h("label", {}, t("New password"), pw), h("label", {}, t("Again"), pw2), h("button", { class: "primary", type: "submit" }, t("Save"))),
    msg));
}

/** the 6-digit code from the authenticator app, after the password */
export async function mfaChallenge(root: HTMLElement, brand: Brand, done: () => void) {
  const { data } = await sb.auth.mfa.listFactors();
  const factor = data?.totp?.[0];
  const code = h("input", { inputmode: "numeric", autocomplete: "one-time-code", maxlength: 6, required: true, dir: "ltr" });
  const msg = h("p", { class: "muted" });
  const logo = logoUrl(brand.logo_path);
  mount(root, h("main", { class: "card narrow" }, logo && h("img", { class: "logo", src: logo, alt: "" }),
    h("h1", {}, t("Two-step sign-in")),
    h("p", {}, t("Enter the 6-digit code from your authenticator app.")),
    h("form", { onsubmit: async (e: Event) => {
      e.preventDefault();
      if (!factor) { msg.textContent = t("No authenticator is set up for this account. Ask an admin to reset it."); return; }
      const { error } = await sb.auth.mfa.challengeAndVerify({ factorId: factor.id, code: code.value.trim() });
      if (error) { msg.textContent = error.message; return; }
      done();
    } }, h("label", {}, t("Code"), code), h("button", { class: "primary", type: "submit" }, t("Continue"))),
    h("button", { class: "link", onclick: async () => { await sb.auth.signOut(); done(); } }, t("Sign out")),
    msg));
  code.focus();
}

/** the My account screen (inside the app) */
export async function account(view: HTMLElement, role: string | null) {
  const { data: { user } } = await sb.auth.getUser();
  const box = h("section", { class: "page" });
  mount(view, box);

  async function render() {
    const { data: f } = await sb.auth.mfa.listFactors();
    const verified = (f?.totp ?? []).filter((x) => x.status === "verified");
    const { data: me } = role ? await sb.from("user_roles").select("name").eq("user_id", user!.id).maybeSingle() : { data: null };
    const name = h("input", { value: me?.name ?? "", placeholder: user?.email ?? "" });
    const pw = h("input", { type: "password", autocomplete: "new-password", minlength: 10 });
    const mfa = h("div", {});
    mount(box,
      h("h2", {}, t("My account")),
      h("p", { class: "muted", dir: "ltr" }, `${user?.email ?? ""}${role ? " · " + role : ""}`),
      role && h("div", { class: "card-in" }, h("label", {}, t("Name shown to the team"), name),
        h("button", { onclick: async () => {
          try { await rpc("set_user_name", { p_user: user!.id, p_name: name.value }); toast(t("Saved")); } catch (e) { toast((e as Error).message, true); }
        } }, t("Save"))),
      h("div", { class: "card-in" }, h("label", {}, t("Language"),
        h("select", { onchange: (e: Event) => { setLang((e.target as HTMLSelectElement).value as "en" | "ar"); location.reload(); } },
          h("option", { value: "en", selected: getLang() === "en" }, "English"), h("option", { value: "ar", selected: getLang() === "ar" }, "العربية")))),
      h("div", { class: "card-in" }, h("label", {}, t("New password"), pw),
        h("button", { onclick: async () => {
          if (!strong(pw.value)) return toast(t("At least 10 characters."), true);
          const { error } = await sb.auth.updateUser({ password: pw.value });
          if (error) return toast(error.message, true);
          pw.value = ""; toast(t("Password saved"));
        } }, t("Change password"))),
      h("div", { class: "card-in" }, h("h3", {}, t("Two-step sign-in")), mfa));
    if (verified.length) {
      mount(mfa, h("p", {}, "✓ " + t("On: you enter a code from your authenticator app when you sign in.")),
        h("button", { onclick: async () => {
          if (!confirm(t("Turn off two-step sign-in for your account?"))) return;
          for (const x of verified) { const { error } = await sb.auth.mfa.unenroll({ factorId: x.id }); if (error) return toast(error.message, true); }
          render();
        } }, t("Turn off")));
    } else {
      mount(mfa, h("p", { class: "muted" }, t("Protects the account with a code from an app on your phone (Google Authenticator, Microsoft Authenticator, 1Password …).")),
        h("button", { class: "primary", onclick: () => enroll(mfa) }, t("Set up")));
    }
  }

  async function enroll(where: HTMLElement) {
    // a half-finished set-up from before is removed first (Supabase keeps it as "unverified")
    const { data: f } = await sb.auth.mfa.listFactors();
    for (const x of (f?.all ?? []).filter((x) => x.status !== "verified")) await sb.auth.mfa.unenroll({ factorId: x.id });
    const { data, error } = await sb.auth.mfa.enroll({ factorType: "totp", friendlyName: "Authenticator " + new Date().toISOString().slice(0, 10) });
    if (error || !data) return toast(error?.message ?? "enroll failed", true);
    const code = h("input", { inputmode: "numeric", autocomplete: "one-time-code", maxlength: 6, dir: "ltr" });
    mount(where,
      h("p", {}, t("1. Scan this with your authenticator app (or type the key).")),
      h("img", { class: "qr", src: data.totp.qr_code, alt: "QR code" }),
      h("p", { class: "muted" }, h("code", { dir: "ltr" }, data.totp.secret)),
      h("label", {}, t("2. The 6-digit code it shows"), code),
      h("button", { class: "primary", onclick: async () => {
        const { error: e } = await sb.auth.mfa.challengeAndVerify({ factorId: data.id, code: code.value.trim() });
        if (e) return toast(e.message, true);
        toast(t("Two-step sign-in is on"));
        render();
      } }, t("Turn on")));
  }
  await render();
}
